// OptimizedDeviceList.jsx
// Optimized device list component with virtualization and memoization
// Part of Device Management Overhaul - Phase 4: Performance Optimization
// Updated: Using itemData pattern for optimal react-window performance

import React, { memo, useMemo, useCallback, useRef } from 'react';
import { FixedSizeList as List, areEqual } from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  Checkbox,
  Skeleton
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  NavigateNext as NavigateIcon,
  CheckCircle as OnlineIcon,
  Error as OfflineIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';

// Memoized device row component - only re-renders when device or selection changes
const DeviceRow = memo(({
  device,
  index,
  style,
  isSelected,
  onSelect,
  onEdit,
  onDelete,
  onNavigate,
  getDeviceIcon,
  hasEditPermission,
  hasDeletePermission,
  showCheckbox
}) => {
  // Check if device is clearvox_user type - these don't have online/offline state
  const isClearvoxUser = useMemo(() => {
    const deviceType = (device.deviceTypeModel?.type || device.deviceTypeModel?.name || device.deviceTypeModel?.friendlyName || '').toLowerCase();
    return deviceType === 'clearvox_user';
  }, [device.deviceTypeModel?.type, device.deviceTypeModel?.name, device.deviceTypeModel?.friendlyName]);

  // Memoize status chip to prevent re-creation
  const statusChip = useMemo(() => {
    // Skip status for clearvox_user devices
    if (isClearvoxUser) return null;

    const status = device.config?.status || device.status || 'Unknown';
    const statusLower = status.toLowerCase();

    let color = 'default';
    let icon = <WarningIcon />;

    if (['registered', 'online', 'active'].includes(statusLower)) {
      color = 'success';
      icon = <OnlineIcon />;
    } else if (['offline', 'inactive', 'disconnected'].includes(statusLower)) {
      color = 'error';
      icon = <OfflineIcon />;
    }

    return (
      <Chip
        icon={icon}
        label={status}
        color={color}
        size="small"
        variant="outlined"
      />
    );
  }, [device.config?.status, device.status, isClearvoxUser]);

  // Memoize offline duration calculation
  const offlineDuration = useMemo(() => {
    // Skip offline duration for clearvox_user devices
    if (isClearvoxUser) return null;

    if (!device.last_seen) return null;

    const status = (device.config?.status || device.status || '').toLowerCase();
    if (['online', 'active', 'registered'].includes(status)) return null;

    try {
      return formatDistanceToNow(new Date(device.last_seen), { addSuffix: true });
    } catch (e) {
      return null;
    }
  }, [device.last_seen, device.config?.status, device.status, isClearvoxUser]);

  // Prevent re-creation of callbacks
  const handleSelect = useCallback((e) => {
    e.stopPropagation();
    onSelect(device.id);
  }, [device.id, onSelect]);

  const handleEdit = useCallback((e) => {
    e.stopPropagation();
    onEdit(device);
  }, [device, onEdit]);

  const handleDelete = useCallback((e) => {
    e.stopPropagation();
    onDelete(device);
  }, [device, onDelete]);

  const handleNavigate = useCallback(() => {
    onNavigate(device);
  }, [device, onNavigate]);

  return (
    <div style={style}>
      <Card
        sx={{
          mx: 2,
          my: 1,
          cursor: 'pointer',
          backgroundColor: isSelected ? 'action.selected' : 'transparent',
          '&:hover': { backgroundColor: 'action.hover' },
          transition: 'background-color 0.2s'
        }}
        onClick={handleNavigate}
      >
        <CardContent sx={{ py: 1.5, px: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {showCheckbox && (
              <Checkbox
                checked={isSelected}
                onChange={handleSelect}
                onClick={(e) => e.stopPropagation()}
                size="small"
              />
            )}

            <Box sx={{ mr: 1.5 }}>
              {getDeviceIcon(device.deviceTypeModel)}
            </Box>

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body1" noWrap sx={{ fontWeight: 500 }}>
                {device.friendlyName || `Device ${device.id}`}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap>
                {device.deviceTypeModel?.friendlyName || 'Unknown Type'}
                {device.locationModel?.friendlyName && (
                  <> • {device.locationModel.friendlyName}</>
                )}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {statusChip}

              {offlineDuration && (
                <Typography variant="caption" color="error" sx={{ minWidth: 100 }}>
                  Offline {offlineDuration}
                </Typography>
              )}

              {hasEditPermission && (
                <Tooltip title="Edit Device">
                  <IconButton size="small" onClick={handleEdit}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}

              {hasDeletePermission && (
                <Tooltip title="Delete Device">
                  <IconButton size="small" onClick={handleDelete} color="error">
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}

              <Tooltip title="View Details">
                <IconButton size="small">
                  <NavigateIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </div>
  );
}, (prevProps, nextProps) => {
  // Custom comparison function for memo
  return (
    prevProps.device.id === nextProps.device.id &&
    prevProps.device.status === nextProps.device.status &&
    prevProps.device.config?.status === nextProps.device.config?.status &&
    prevProps.device.last_seen === nextProps.device.last_seen &&
    prevProps.isSelected === nextProps.isSelected &&
    prevProps.showCheckbox === nextProps.showCheckbox &&
    prevProps.hasEditPermission === nextProps.hasEditPermission &&
    prevProps.hasDeletePermission === nextProps.hasDeletePermission
  );
});

DeviceRow.displayName = 'DeviceRow';

// Loading skeleton for virtual list
const LoadingSkeleton = memo(({ style }) => (
  <div style={style}>
    <Card sx={{ mx: 2, my: 1 }}>
      <CardContent sx={{ py: 1.5, px: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Skeleton variant="circular" width={40} height={40} />
          <Box sx={{ flex: 1 }}>
            <Skeleton variant="text" width="60%" />
            <Skeleton variant="text" width="40%" height={16} />
          </Box>
          <Skeleton variant="rectangular" width={80} height={24} />
        </Box>
      </CardContent>
    </Card>
  </div>
));

LoadingSkeleton.displayName = 'LoadingSkeleton';

// Row renderer component - receives data via itemData pattern
// This pattern prevents re-renders when parent state changes
const Row = memo(({ index, style, data }) => {
  const {
    devices,
    selectedDevicesSet,
    onDeviceSelect,
    onDeviceEdit,
    onDeviceDelete,
    onDeviceNavigate,
    getDeviceIcon,
    hasEditPermission,
    hasDeletePermission,
    showCheckbox,
    isLoading
  } = data;

  if (isLoading) {
    return <LoadingSkeleton style={style} />;
  }

  const device = devices[index];
  if (!device) return null;

  // Use Set for O(1) lookup instead of array.includes O(n)
  const isSelected = selectedDevicesSet.has(device.id);

  return (
    <DeviceRow
      device={device}
      index={index}
      style={style}
      isSelected={isSelected}
      onSelect={onDeviceSelect}
      onEdit={onDeviceEdit}
      onDelete={onDeviceDelete}
      onNavigate={onDeviceNavigate}
      getDeviceIcon={getDeviceIcon}
      hasEditPermission={hasEditPermission}
      hasDeletePermission={hasDeletePermission}
      showCheckbox={showCheckbox}
    />
  );
}, areEqual);

Row.displayName = 'VirtualRow';

// Main optimized device list component
const OptimizedDeviceList = memo(({
  devices,
  selectedDevices = [],
  onDeviceSelect,
  onDeviceEdit,
  onDeviceDelete,
  onDeviceNavigate,
  getDeviceIcon,
  hasEditPermission,
  hasDeletePermission,
  showCheckbox = false,
  isLoading = false,
  itemHeight = 80
}) => {
  // Convert selectedDevices array to Set for O(1) lookup
  // Memoize to prevent recreation on every render
  const selectedDevicesSet = useMemo(
    () => new Set(selectedDevices),
    [selectedDevices]
  );

  // Memoize itemData to prevent Row re-renders
  // Only changes when one of its dependencies actually changes
  const itemData = useMemo(() => ({
    devices,
    selectedDevicesSet,
    onDeviceSelect,
    onDeviceEdit,
    onDeviceDelete,
    onDeviceNavigate,
    getDeviceIcon,
    hasEditPermission,
    hasDeletePermission,
    showCheckbox,
    isLoading
  }), [
    devices,
    selectedDevicesSet,
    onDeviceSelect,
    onDeviceEdit,
    onDeviceDelete,
    onDeviceNavigate,
    getDeviceIcon,
    hasEditPermission,
    hasDeletePermission,
    showCheckbox,
    isLoading
  ]);

  // Calculate list size
  const itemCount = isLoading ? 10 : devices.length;

  // Stable key extractor - use device id for stable keys
  const itemKey = useCallback((index, data) => {
    if (data.isLoading) return `loading-${index}`;
    const device = data.devices[index];
    return device?.id || `device-${index}`;
  }, []);

  if (!isLoading && devices.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="body1" color="text.secondary">
          No devices found
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', width: '100%' }}>
      <AutoSizer>
        {({ height, width }) => (
          <List
            height={height}
            itemCount={itemCount}
            itemSize={itemHeight}
            width={width}
            overscanCount={5}
            itemData={itemData}
            itemKey={itemKey}
          >
            {Row}
          </List>
        )}
      </AutoSizer>
    </Box>
  );
});

OptimizedDeviceList.displayName = 'OptimizedDeviceList';

export default OptimizedDeviceList;